import { type TextEditorHistoryEntry, type UndoBoundary } from './types';

/**
 * 单个撤销或重做栈节点。
 */
type Entry = {
    value: TextEditorHistoryEntry;
};

/**
 * 撤销栈和重做栈的最大节点数量。
 */
const MAX_STACK_SIZE = 100;

/**
 * 普通连续输入可合并为同一撤销节点的最长间隔。
 */
const TYPING_MERGE_MS = 200;

/**
 * 文本编辑器会话内的轻量撤销重做历史。
 */
export class TextEditorHistory {
    private undoStack: Entry[] = [];
    private redoStack: Entry[] = [];

    /**
     * 当前是否处于可合并的连续输入片段。
     *
     * 如果这次和上一笔都是普通输入，并且间隔不超过 `TYPING_MERGE_MS`，
     * 则只更新当前节点，避免每个字符都成为一次独立撤销。
     */
    private isMergingTyping = false;

    /**
     * 最近一次写入历史的时间戳。
     */
    private lastChangeAt = 0;

    /**
     * 创建文本编辑历史。
     *
     * @param current - 初始历史节点。
     */
    constructor(private current: TextEditorHistoryEntry) {}

    /**
     * 获取当前历史节点。
     *
     * @returns 当前历史节点。
     */
    getCurrent() {
        return this.current;
    }

    /**
     * 推入新的历史节点，并按撤销边界决定是否合并连续输入。
     *
     * @param value - 新的历史节点。
     * @param boundary - 撤销边界。
     */
    push(value: TextEditorHistoryEntry, boundary: UndoBoundary = 'typing') {
        const now = Date.now();
        const mergeTyping = boundary === 'typing' && this.isMergingTyping && now - this.lastChangeAt <= TYPING_MERGE_MS;

        if (!mergeTyping) {
            this.undoStack.push({ value: this.current });
            if (this.undoStack.length > MAX_STACK_SIZE) this.undoStack.shift();
        }

        this.current = value;
        this.redoStack = [];
        this.isMergingTyping = boundary === 'typing';
        this.lastChangeAt = now;
    }

    /**
     * 结束当前连续输入片段，后续输入会创建新的历史节点。
     */
    seal() {
        this.isMergingTyping = false;
    }

    /**
     * 替换当前历史节点，不新增撤销节点。
     *
     * @param value - 新的历史节点。
     */
    replaceCurrent(value: TextEditorHistoryEntry) {
        this.current = value;
    }

    /**
     * 撤销到上一历史节点。
     *
     * @returns 撤销后的历史节点；没有可撤销节点时返回 null。
     */
    undo() {
        const entry = this.undoStack.pop();
        if (!entry) return null;

        this.redoStack.push({ value: this.current });
        if (this.redoStack.length > MAX_STACK_SIZE) this.redoStack.shift();

        this.current = entry.value;
        this.seal();

        return this.current;
    }

    /**
     * 重做到下一历史节点。
     *
     * @returns 重做后的历史节点；没有可重做节点时返回 null。
     */
    redo() {
        const entry = this.redoStack.pop();
        if (!entry) return null;

        this.undoStack.push({ value: this.current });
        if (this.undoStack.length > MAX_STACK_SIZE) this.undoStack.shift();

        this.current = entry.value;
        this.seal();

        return this.current;
    }
}

export { getTypingBoundary } from './utils';
export type { TextEditorHistoryEntry, UndoBoundary } from './types';
